import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MessagesList from '../messages/MessagesList';
import RecordingIndicator from './RecordingIcon';

import {
  MenuItem,
  Select,
  FormControl,
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  Toolbar,
} from '@mui/material';

const RecordingConversation = ({
  selectedConversation,
  startRecording,
  stopRecording,
  recording,
  language,
  setLanguage,
  selectedPrompt,
  setSelectedPrompt,
  promptTemplates,
  recordingDuration,
  wordCount,
  isMobile = false,
  messages = [],
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')) || isMobile;

  // Pre-select the first prompt template if none is selected and templates are available
  useEffect(() => {
    if (
      (!selectedPrompt || selectedPrompt === '') &&
      promptTemplates &&
      promptTemplates.length > 0
    ) {
      setSelectedPrompt(promptTemplates[0].id);
    }
  }, [promptTemplates, selectedPrompt, setSelectedPrompt]);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Check if recording can be started
  const canStartRecording = language && selectedPrompt && !recording;

  // Recording Controls Component (Dropdowns and Button)
  const RecordingControlsSection = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSmallScreen ? 'column' : 'row',
        alignItems: isSmallScreen ? 'stretch' : 'center',
        gap: {xs: 1, xl: 3},
        mb: {xs: 1, xl: 4},
        mt: isSmallScreen ? 0 : 8,
        width: '100%',
        justifyContent: 'center',
        ...(isSmallScreen && {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          px: 3,
          py: 3,
          backgroundColor: '#F9F9F9',
        }),
      }}
    >
      {/* Language Dropdown */}
      <FormControl
        sx={{
          width: {xs: '100%', md: 180, lg: 220, xl: 280},
          height: 40,
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            borderColor: '#E4E4E7',
            backgroundColor: '#ffffff',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E4E4E7',
          },
        }}
      >
        <Select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          disabled={recording}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
              },
            },
          }}
        >
          <MenuItem value='de'>{t('languages.de')}</MenuItem>
          <MenuItem value='en-US'>{t('languages.en')}</MenuItem>
        </Select>
      </FormControl>

      {/* Prompt Template Dropdown */}
      <FormControl
        sx={{
          width: {xs: '100%', md: 180, lg: 220, xl: 280},
          height: 40,
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            borderColor: '#E4E4E7',
            backgroundColor: '#ffffff',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E4E4E7',
          },
        }}
      >
        <Select
          value={selectedPrompt}
          onChange={(e) => setSelectedPrompt(e.target.value)}
          disabled={recording}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
              },
            },
          }}
        >
          {promptTemplates &&
            promptTemplates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.template_name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Recording Button */}
      <Button
        variant='buttonStandard'
        onClick={recording ? stopRecording : startRecording}
        disabled={recording ? false : !canStartRecording}
        sx={{
          width: isSmallScreen ? '100%' : 'auto',
          px: { md: 2, lg: 4 },
          height: 39,
          fontSize: 14,
          backgroundColor: recording
            ? theme.palette.secondary.main
            : theme.palette.primary.main,
        }}
      >
        {recording
          ? t('recording.stopRecording')
          : t('recording.startRecording')}
      </Button>
    </Box>
  );

  // Recording Info Component (Timer, Indicator, Word Count)
  const RecordingInfoSection = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        mt: {xs: 5, xl: 10},
        mb: {xs: 2, xl: 8},
        gap: {xs: 1, xl: 2},
      }}
    >
      {/* Recording Timer and Indicator */}
      <RecordingIndicator
        recording={recording}
        outerSize={isSmallScreen ? 116 : 140}
        innerSize={isSmallScreen ? 96 : 116}
        iconSize={isSmallScreen ? '56px' : '70px'}
      />

      <Box sx={{ textAlign: 'center', my: '16px' }}>
        <Typography
          variant='h1'
          sx={{
            fontFamily: 'Geist Mono',
            fontSize: 40,
            fontWeight: 400,
            letterSpacing: '1%',
            lineHeight: 1,
            color: theme.palette.primary.main,
          }}
        >
          {formatDuration(recordingDuration)}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            fontSize: 12,
            fontWeight: 400,
            color: theme.palette.primary.light,
            mt: 1,
          }}
        >
          {t('recording.recordingTime')}
        </Typography>
      </Box>

      {/* Word Count */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant='h2'
          sx={{
            fontFamily: 'Geist Mono',
            fontSize: 24,
            fontWeight: 400,
            color: theme.palette.primary.main,
            lineHeight: 1,
          }}
        >
          {wordCount}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            fontSize: 12,
            fontWeight: 400,
            color: theme.palette.primary.light,
            mt: 1,
          }}
        >
          {t('common.words')}
        </Typography>
      </Box>
    </Box>
  );

  // Messages List Component
  const MessagesListSection = () => (
    <Box
      sx={{
        width: '100%',
        maxWidth: 520,
        mx: 'auto',
        mb: 4,
        pb: isSmallScreen ? '160px' : 0,
        px: isSmallScreen ? 2 : 0,
      }}
    >
      <MessagesList messages={messages} maxVisible={isSmallScreen ? 2 : 4} />
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#F9F9F9',
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            width: '100%',
            borderBottom: '1px solid #e0e0e0',
            zIndex: 10,
            bgcolor: '#ffff',
            height: '68px',
          }}
        >
          <Toolbar sx={{ px: 2 }}>
            <Typography variant='h2' component='h2' sx={{ flexGrow: 1 }}>
              {t('recording.title')}
            </Typography>

            {/* Language Selector Dropdown */}
            <FormControl size='small' sx={{ minWidth: 100, mr: 2 }}>
              <Select
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                variant='standard'
                disableUnderline
                sx={{
                  '& .MuiSelect-select': {
                    paddingBottom: 0,
                    fontSize: 14,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem value='en'>{t('languages.en')}</MenuItem>
                <MenuItem value='de'>{t('languages.de')}</MenuItem>
              </Select>
            </FormControl>

            {/* <IconButton aria-label='theme'>
              <Brightness4Icon />
            </IconButton> */}
          </Toolbar>
        </Box>
      )}

      {/* Conditionally render sections based on screen size */}
      {isSmallScreen ? (
        <>
          <RecordingInfoSection />
          <MessagesListSection />
          <RecordingControlsSection />
        </>
      ) : (
        <>
          <RecordingControlsSection />
          <RecordingInfoSection />
          <MessagesListSection />
        </>
      )}
    </Box>
  );
};

export default RecordingConversation;
